// Iter AV — lock screen + inactivity / treatment-window dialogs.
import { useState } from "react";
import { Lock, ShieldAlert, TimerReset } from "lucide-react";
import { biometricLabel, BiometricIcon } from "../../lib/biometrics";
import { useSecurity } from "../../context/SecurityContext";
import { useAuth } from "../../context/AuthContext";
import { passkeysSupported, hasLocalPasskeyHint } from "../../lib/webauthn";

export function LockScreen() {
  const { unlock, unlockBiometric, signOutNow } = useSecurity();
  const { user } = useAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const showBiometric = passkeysSupported() && hasLocalPasskeyHint();

  const submit = async (e) => {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError("");
    const r = await unlock(password);
    setBusy(false);
    if (!r.ok) { setError(r.error); setPassword(""); }
  };

  const biometric = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    const r = await unlockBiometric();
    setBusy(false);
    if (!r.ok) setError(r.error);
  };

  return (
    <div className="min-h-screen bg-paper-50 flex items-center justify-center px-6" data-testid="lock-screen">
      <div className="w-full max-w-sm text-center">
        <span className="w-14 h-14 rounded-2xl bg-ink-900 text-white inline-flex items-center justify-center mb-5">
          <Lock className="w-6 h-6" strokeWidth={1.5} />
        </span>
        <h1 className="font-heading text-3xl text-ink-900 mb-2">Posturra is locked</h1>
        <p className="text-ink-500 text-sm mb-8">
          Client information is hidden for privacy. Enter your password to continue{user?.email ? ` as ${user.email}` : ""}.
        </p>
        <form onSubmit={submit} className="space-y-3">
          {showBiometric && (
            <button
              type="button"
              onClick={biometric}
              disabled={busy}
              data-testid="lock-biometric-btn"
              className="w-full min-h-[52px] rounded-full bg-ink-900 hover:bg-ink-900/90 disabled:opacity-50 text-white font-medium transition-all inline-flex items-center justify-center gap-2"
            >
              <BiometricIcon className="w-5 h-5" /> {biometricLabel("Unlock")}
            </button>
          )}
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            data-testid="lock-password-input"
            className="w-full min-h-[52px] px-5 rounded-full bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-ink-900 text-center"
          />
          {error && <div className="text-sm text-terracotta-600" data-testid="lock-error">{error}</div>}
          <button
            type="submit"
            disabled={busy || !password}
            data-testid="lock-unlock-btn"
            className="w-full min-h-[52px] rounded-full bg-sage-500 hover:bg-sage-600 disabled:opacity-50 text-white font-medium transition-all"
          >
            {busy ? "Unlocking…" : "Unlock"}
          </button>
        </form>
        <button
          type="button"
          onClick={signOutNow}
          data-testid="lock-signout-btn"
          className="mt-6 text-sm text-ink-500 hover:text-ink-900 underline underline-offset-4"
        >
          Sign out instead
        </button>
      </div>
    </div>
  );
}

function DialogShell({ children, testid }) {
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center px-4" data-testid={testid}>
      <div className="absolute inset-0 bg-ink-900/50 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-white rounded-3xl border border-paper-200 shadow-2xl p-6 sm:p-8">
        {children}
      </div>
    </div>
  );
}

export function IdleWarningDialog() {
  const { staySignedIn, signOutNow } = useSecurity();
  return (
    <DialogShell testid="idle-warning-dialog">
      <div className="flex items-center gap-3 mb-3">
        <span className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
          <ShieldAlert className="w-5 h-5" strokeWidth={1.5} />
        </span>
        <h2 className="font-heading text-xl text-ink-900">Session Expiring</h2>
      </div>
      <p className="text-ink-700 mb-6">For your security, you will be signed out in 1 minute due to inactivity.</p>
      <div className="flex flex-wrap gap-3">
        <button
          onClick={staySignedIn}
          data-testid="stay-signed-in-btn"
          className="flex-1 min-h-[48px] px-5 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium transition-all"
        >
          Stay Signed In
        </button>
        <button
          onClick={signOutNow}
          data-testid="sign-out-now-btn"
          className="min-h-[48px] px-5 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-700 transition-all"
        >
          Sign Out Now
        </button>
      </div>
    </DialogShell>
  );
}

export function WindowEndingDialog() {
  const { windowInfo, extendWindow, endWindow, lockNow } = useSecurity();
  const minutesLeft = windowInfo ? Math.max(1, Math.ceil((windowInfo.expiresAtMs - Date.now()) / 60000)) : 5;
  return (
    <DialogShell testid="window-ending-dialog">
      <div className="flex items-center gap-3 mb-3">
        <span className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
          <TimerReset className="w-5 h-5" strokeWidth={1.5} />
        </span>
        <h2 className="font-heading text-xl text-ink-900">Treatment Session Ending</h2>
      </div>
      <p className="text-ink-700 mb-6">
        This treatment session will lock in {minutesLeft} minute{minutesLeft === 1 ? "" : "s"}. Extend the session or finish your documentation.
      </p>
      <div className="flex flex-wrap gap-3">
        <button
          onClick={extendWindow}
          data-testid="extend-window-btn"
          className="flex-1 min-h-[48px] px-5 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium transition-all"
        >
          Extend 15 Minutes
        </button>
        <button
          onClick={endWindow}
          data-testid="finish-window-btn"
          className="min-h-[48px] px-5 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-700 transition-all"
        >
          Finish Session
        </button>
        <button
          onClick={lockNow}
          data-testid="window-lock-now-btn"
          className="min-h-[48px] px-5 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-700 transition-all"
        >
          Lock Now
        </button>
      </div>
    </DialogShell>
  );
}

// Gate rendered inside Protected: unmounts ALL PHI while locked.
export function SecurityGate({ children }) {
  const { locked, idleWarning, windowWarning } = useSecurity();
  if (locked) return <LockScreen />;
  return (
    <>
      {idleWarning && <IdleWarningDialog />}
      {windowWarning && <WindowEndingDialog />}
      {children}
    </>
  );
}
